import {createContext, useContext, useEffect, useState, useCallback} from 'react';
import axios from "axios";
import {useCookies} from "react-cookie";
import {WebSocketContext} from "./WebSocketContext";

const TokenContext = createContext(null)

// Configuration sécurisée des cookies
const COOKIE_OPTIONS = {
    httpOnly: false,
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'lax', // 'lax' au lieu de 'strict' pour permettre le callback OAuth
    path: '/',
    maxAge: 60 * 60 * 24 * 7 // 7 jours
};

// Utilitaire pour générer un state sécurisé
const generateSecureState = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Validation des tokens
const validateToken = (token) => {
    if (!token || typeof token !== 'string') return false;

    try {
        // Si ce n'est pas un JWT (pas de points), on considère que c'est un token opaque valide
        if (!token.includes('.')) {
            console.log('🔍 Token opaque détecté (non-JWT) - considéré comme valide');
            return true;
        }

        // Vérification basique du format JWT
        const parts = token.split('.');
        if (parts.length !== 3) return false;

        // Décodage du payload pour vérifier l'expiration
        const payload = JSON.parse(atob(parts[1]));
        const now = Math.floor(Date.now() / 1000);

        return payload.exp > now;
    } catch (error) {
        console.error('Token validation error:', error);
        // Si on ne peut pas décoder le token (token opaque), on le considère comme valide
        console.log('🔍 Token non-JWT ou erreur de décodage - considéré comme valide');
        return true;
    }
};

function TokenProvider({children}) {
    const _webSocketContext = useContext(WebSocketContext)

    const [cookies, setCookie, removeCookie] = useCookies(['DASHBOARD_ACCESS', 'DASHBOARD_REFRESH', 'DASHBOARD_EXPIRE', 'DASHBOARD_STATE']);
    const serverOauth = process.env.REACT_APP_SERVER_URL_API

    const urlCode = '/api/auth/code'
    const urlAuthorize = '/api/auth/authorize'
    const urlToken = '/api/auth/token'

    // État d'authentification  
    const [authState] = useState(() => {
        const existingState = cookies.DASHBOARD_STATE;
        if (existingState) {
            console.log('🔑 State existant trouvé dans les cookies');
            return existingState;
        }

        const newState = generateSecureState();
        console.log('🔑 Génération d\'un nouveau state sécurisé');
        return newState;
    });
    const [accessToken, setAccessToken] = useState(() => {
        const token = cookies.DASHBOARD_ACCESS;
        console.log('🔑 [TokenContext] Initialisation accessToken depuis cookies');
        console.log('📋 [TokenContext] Token trouvé:', token ? token.substring(0, 20) + '...' : 'AUCUN');
        console.log('✅ [TokenContext] Token validé:', validateToken(token));
        return validateToken(token) ? token : '';
    })
    const [refreshToken, setRefreshToken] = useState(() => {
        return cookies.DASHBOARD_REFRESH || '';
    })
    const [expireAt, setExpireAt] = useState(() => {
        return cookies.DASHBOARD_EXPIRE ? parseInt(cookies.DASHBOARD_EXPIRE) : 0;
    })

    // Synchroniser l'accessToken avec les cookies quand ils changent
    useEffect(() => {
        const cookieToken = cookies.DASHBOARD_ACCESS;
        if (cookieToken && cookieToken !== accessToken) {
            console.log('🔄 [TokenContext] Synchronisation du token depuis les cookies');
            console.log('📋 [TokenContext] Nouveau token:', cookieToken.substring(0, 20) + '...');
            if (validateToken(cookieToken)) {
                setAccessToken(cookieToken);
            }
        }
    }, [cookies.DASHBOARD_ACCESS])

    const [finishProcess, setFinishProcess] = useState(() => {
        // Si on a déjà un token valide au chargement, considérer le processus comme terminé
        const token = cookies.DASHBOARD_ACCESS;
        const hasValidToken = validateToken(token);
        console.log('🏁 [TokenContext] Initialisation finishProcess:', hasValidToken);
        return hasValidToken;
    })
    const [authError, setAuthError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    // Circuit breaker pour éviter le spam de refresh token
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [refreshAttempts, setRefreshAttempts] = useState(0)
    const [lastRefreshAttempt, setLastRefreshAttempt] = useState(0)

    // Fonction pour vérifier si on est sur la page de callback
    const isOnCallbackPage = () => {
        return window.location.pathname === '/authentication/callback';
    }

    // Toutes les fonctions de base d'abord
    const saveToken = useCallback((access, refresh, expire) => {
        console.log('💾 [TokenContext] Sauvegarde des tokens...');
        console.log('📋 [TokenContext] Access token:', access ? access.substring(0, 20) + '...' : 'MANQUANT');
        console.log('📋 [TokenContext] Refresh token:', refresh ? refresh.substring(0, 20) + '...' : 'MANQUANT');
        console.log('⏰ [TokenContext] Expire:', expire);

        if (!validateToken(access)) {
            console.error('❌ [TokenContext] Token d\'accès invalide lors de la sauvegarde');
            throw new Error('Token d\'accès invalide');
        }

        console.log('✅ [TokenContext] Token validé, mise à jour du state');
        setAccessToken(access)
        setRefreshToken(refresh)
        setExpireAt(expire)

        console.log('🍪 [TokenContext] Sauvegarde dans les cookies avec options:', COOKIE_OPTIONS);
        // Sauvegarde sécurisée dans les cookies
        setCookie("DASHBOARD_ACCESS", access, COOKIE_OPTIONS)
        setCookie("DASHBOARD_REFRESH", refresh, COOKIE_OPTIONS)
        setCookie("DASHBOARD_EXPIRE", expire.toString(), COOKIE_OPTIONS)

        console.log('✅ [TokenContext] Tokens sauvegardés avec succès');
        console.log('📋 [TokenContext] Nouveau accessToken dans state:', access.substring(0, 20) + '...');

        // Vérifier immédiatement que le cookie est bien créé
        setTimeout(() => {
            console.log('🔍 [TokenContext] Vérification des cookies après sauvegarde');
            console.log('🍪 [TokenContext] DASHBOARD_ACCESS dans cookies:', document.cookie.includes('DASHBOARD_ACCESS') ? 'OUI' : 'NON');
            console.log('📋 [TokenContext] Tous les cookies:', document.cookie);
        }, 100);
    }, [setCookie])

    const clearAuth = useCallback(() => {
        console.log('Nettoyage de l\'authentification');

        setAccessToken('')
        setRefreshToken('')
        setExpireAt(0)
        setFinishProcess(false)
        setAuthError(null)

        // Nettoyage des cookies
        removeCookie("DASHBOARD_ACCESS")
        removeCookie("DASHBOARD_REFRESH")
        removeCookie("DASHBOARD_EXPIRE")
        removeCookie("DASHBOARD_STATE")
    }, [removeCookie])

    const startOAuthFlow = useCallback(() => {
        console.log('🚀 Démarrage du flux OAuth2');

        // Sauvegarder le state sécurisé dans les cookies AVANT la redirection
        setCookie("DASHBOARD_STATE", authState, {
            ...COOKIE_OPTIONS,
            maxAge: 60 * 10 // 10 minutes seulement pour le state
        });

        console.log('💾 State sauvegardé:', authState.substring(0, 10) + '...');

        // Construire l'URL avec le state
        const oauthUrl = `${serverOauth}${urlCode}?state=${encodeURIComponent(authState)}`;
        console.log('🌐 Redirection vers:', oauthUrl);

        // Redirection immédiate vers le serveur OAuth
        window.location.href = oauthUrl;
    }, [authState, setCookie, serverOauth, urlCode]);

    const setCode = useCallback(async (code, receivedState) => {
        if (finishProcess) {
            console.log('❌ Processus déjà terminé, ignorer cette tentative');
            return;
        }

        console.log('=== 🚀 DÉBUT TRAITEMENT CODE D\'AUTORISATION ===');
        console.log('📝 Code:', code?.substring(0, 10) + '...');
        console.log('🔑 State reçu:', receivedState);
        console.log('💾 State stocké:', cookies.DASHBOARD_STATE);
        console.log('🌐 URL actuelle:', window.location.href);

        setIsLoading(true);
        setAuthError(null);

        try {
            // Vérification si c'est un state temporaire/d'urgence
            const isEmergencyState = receivedState && (
                receivedState.startsWith('temp_') ||
                receivedState.startsWith('temp_emergency_')
            );

            if (isEmergencyState) {
                console.warn('⚠️ ===== MODE D\'URGENCE ACTIVÉ =====');
                console.warn('🔧 State temporaire détecté:', receivedState);
                console.warn('📋 Raison: Votre serveur OAuth renvoie un state vide');
                console.warn('💡 Solution: Corrigez votre serveur pour inclure le state dans le callback');
                console.warn('=====================================');
            } else {
                console.log('🔒 Validation normale du state...');
                const storedState = cookies.DASHBOARD_STATE;

                if (!receivedState || receivedState !== storedState) {
                    console.error('❌ ERREUR CSRF DÉTECTÉE !');
                    console.error('Reçu:', JSON.stringify(receivedState));
                    console.error('Attendu:', JSON.stringify(storedState));
                    throw new Error(`State invalide - CSRF détecté. Reçu: "${receivedState}", Attendu: "${storedState}"`);
                }
                console.log('✅ State validé avec succès');
            }

            // Construction de la requête
            const url = serverOauth + urlAuthorize;
            const data = {
                code: code
                // Note: pas de state envoyé si c'est un state d'urgence
            };

            console.log('📡 Envoi vers:', url);
            console.log('📦 Données envoyées:', data);
            console.log('⏱️ Timeout: 15 secondes');

            const response = await axios.post(url, data, {
                timeout: 15000,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            console.log('=== 📨 RÉPONSE SERVEUR ===');
            console.log('✅ Status HTTP:', response.status);
            console.log('📋 Headers:', response.headers);
            console.log('🔑 Keys dans response.data:', Object.keys(response.data || {}));
            console.log('🎫 A un access_token:', !!response.data?.access_token);
            console.log('❌ A une erreur:', !!response.data?.error);

            if (response.data?.access_token) {
                console.log('🎫 Access token reçu (preview):', response.data.access_token.substring(0, 20) + '...');
            }

            if (response.data?.refresh_token) {
                console.log('🔄 Refresh token reçu (preview):', response.data.refresh_token.substring(0, 20) + '...');
            }

            // Vérification des erreurs
            if (response.data?.error) {
                console.error('❌ Erreur dans la réponse:', response.data.error);
                throw new Error(`Erreur serveur: ${response.data.error}`);
            }

            if (!response.data?.access_token) {
                console.error('❌ Token manquant !');
                console.error('📋 Réponse complète:', response.data);
                throw new Error('Token d\'accès manquant dans la réponse serveur');
            }

            console.log('💾 Sauvegarde des tokens...');
            try {
                saveToken(
                    response.data.access_token,
                    response.data.refresh_token,
                    response.data.expire_at
                );
                console.log('✅ Tokens sauvegardés avec succès !');
            } catch (saveError) {
                console.error('❌ Erreur lors de la sauvegarde des tokens:', saveError);
                throw new Error(`Erreur de sauvegarde: ${saveError.message}`);
            }

            // Nettoyer le state après utilisation (sauf si urgence)
            if (!isEmergencyState) {
                console.log('🧹 Nettoyage du state stocké');
                removeCookie("DASHBOARD_STATE");
            }

            setFinishProcess(true);
            console.log('🎉 ===== AUTHENTIFICATION RÉUSSIE ! =====');

        } catch (error) {
            console.error('=== ❌ ERREUR AUTHENTIFICATION ===');
            console.error('🏷️ Type d\'erreur:', error.name);
            console.error('💬 Message:', error.message);
            console.error('🔢 Status HTTP:', error.response?.status);
            console.error('📋 Data de réponse:', error.response?.data);
            console.error('🌐 URL tentée:', error.config?.url);
            console.error('📦 Data envoyée:', error.config?.data);
            console.error('📚 Stack complet:', error.stack);
            console.error('=================================');

            // Extraction du message d'erreur le plus utile
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.response?.data?.error_description ||
                error.message ||
                'Erreur d\'authentification inconnue';

            setAuthError(errorMessage);

            // Ne pas nettoyer en cas d'erreur pour permettre le debug
            console.log('🔍 Authentification échouée - données conservées pour debug');

        } finally {
            setIsLoading(false);
            console.log('=== ⏹️ FIN TRAITEMENT ===');
        }
    }, [finishProcess, cookies.DASHBOARD_STATE, serverOauth, saveToken, removeCookie])

    const disconnect = useCallback(() => {
        try {
            console.log('Déconnexion...');
            clearAuth();

            // Fermeture sécurisée de la connexion WebSocket
            if (_webSocketContext?.ws) {
                _webSocketContext.ws.close();
            }

            // URL de déconnexion sécurisée avec fallback
            const securityUrl = process.env.REACT_APP_SERVER_SECURITY_URL || 'https://security.alais-studio.fr';
            const disconnectUrl = `${securityUrl}/auth/disconnect`;

            console.log('🌐 Redirection de déconnexion vers:', disconnectUrl);
            window.location.href = disconnectUrl;
        } catch (error) {
            console.error('Error during disconnect:', error);
            clearAuth();
            window.location.href = '/';
        }
    }, [clearAuth, _webSocketContext])

    const regenerateAccessToken = useCallback(async () => {
        const now = Date.now();

        // 🛑 CIRCUIT BREAKER: Éviter le spam de refresh token
        if (isRefreshing) {
            console.warn('🛑 Refresh déjà en cours, ignorer cette tentative');
            return;
        }

        // Limite de tentatives (5 max en 30 secondes)
        if (refreshAttempts >= 5 && (now - lastRefreshAttempt) < 30000) {
            console.error('🚨 TROP DE TENTATIVES DE REFRESH - Circuit breaker activé');
            disconnect();
            return;
        }

        // Reset counter après 30 secondes
        if ((now - lastRefreshAttempt) > 30000) {
            setRefreshAttempts(0);
        }

        setIsRefreshing(true);
        setRefreshAttempts(prev => prev + 1);
        setLastRefreshAttempt(now);

        console.log('🔄 === DEBUG REFRESH TOKEN ===');
        console.log('Tentative:', refreshAttempts + 1, '/ 5');
        console.log('refreshToken state:', refreshToken);
        console.log('cookies.DASHBOARD_REFRESH:', cookies.DASHBOARD_REFRESH);

        // CORRECTION: Utiliser le cookie directement si le state est vide
        const actualRefreshToken = refreshToken || cookies.DASHBOARD_REFRESH;
        console.log('actualRefreshToken:', actualRefreshToken ? actualRefreshToken.substring(0, 10) + '...' : 'null');

        if (!actualRefreshToken) {
            console.log('❌ Pas de refresh token (ni state ni cookie), déconnexion');
            setIsRefreshing(false);
            disconnect();
            return;
        }

        console.log('🔄 Renouvellement du token d\'accès...');

        try {
            const url = serverOauth + urlToken
            const data = {
                grant_type: 'refresh_token',
                refresh_token: actualRefreshToken,
                client_id: process.env.REACT_APP_CLIENT_ID || 'AUTOMATE'
            }

            console.log('📦 Envoi refresh token vers:', url);

            const response = await axios.post(url, data, {
                timeout: 10000,
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            console.log('✅ Réponse refresh reçue:', {
                status: response.status,
                hasAccessToken: !!response.data?.access_token,
                hasRefreshToken: !!response.data?.refresh_token
            });

            if (response.data?.error || !response.data?.access_token) {
                throw new Error(response.data?.error || 'Erreur de renouvellement du token');
            }

            saveToken(response.data.access_token, response.data.refresh_token, response.data.expire_at);
            console.log('🎉 Token renouvelé avec succès');

            // Reset counter après succès
            setRefreshAttempts(0);

        } catch (error) {
            console.error('❌ Erreur refresh token:', error.response?.data || error.message);

            // Si c'est un 429 (rate limit), on attend un peu plus
            if (error.response?.status === 429) {
                console.log('⏳ Rate limit détecté, pause plus longue...');
                setTimeout(() => {
                    setIsRefreshing(false);
                }, 5000); // 5 secondes de pause
                return;
            }

            disconnect();
        } finally {
            // Délai minimum entre les refresh (éviter le spam)
            setTimeout(() => {
                setIsRefreshing(false);
            }, 1000);
        }
    }, [refreshToken, serverOauth, saveToken, disconnect, cookies.DASHBOARD_REFRESH, isRefreshing, refreshAttempts, lastRefreshAttempt])

    // Vérification initiale - SIMPLIFIÉ pour éviter les boucles
    useEffect(() => {
        console.log('=== 🔍 USEEFFECT TOKEN PROVIDER ===');
        console.log('🔍 État actuel:', {
            currentPath: window.location.pathname,
            accessToken: !!accessToken,
            refreshToken: !!refreshToken,
            finishProcess,
            isCallbackPage: isOnCallbackPage()
        });

        // PLUS DE REDIRECTION AUTOMATIQUE - laissé à ProtectedRoute
        console.log('✅ TokenProvider initialisé sans redirection automatique');
        console.log('=== 🏁 FIN USEEFFECT TOKEN PROVIDER ===');
    }, []) // Juste une fois au démarrage

    // Gestion automatique du renouvellement des tokens
    useEffect(() => {
        if (expireAt > 0 && accessToken) {
            // Calculer le temps avant expiration, moins 5 minutes de marge
            const renewTime = expireAt - Date.now() - (5 * 60 * 1000); // 5 minutes avant expiration

            if (renewTime > 0) {
                console.log('⏰ Programmation du renouvellement des tokens dans:', Math.round(renewTime / 1000 / 60), 'minutes');
                const timeoutId = setTimeout(() => {
                    console.log('⏰ Renouvellement automatique programmé déclenché');
                    regenerateAccessToken();
                }, renewTime);

                return () => clearTimeout(timeoutId);
            } else {
                console.log('⚠️ Token expiré ou expire bientôt, renouvellement immédiat');
                regenerateAccessToken();
            }
        }
    }, [expireAt, accessToken, regenerateAccessToken])

    // Note: Le test de refresh automatique a été supprimé pour la production

    // Intercepteur axios pour gérer automatiquement les tokens
    useEffect(() => {
        if (accessToken) {
            const requestInterceptor = axios.interceptors.request.use(
                (config) => {
                    if (validateToken(accessToken)) {
                        config.headers.Authorization = `Bearer ${accessToken}`;
                    }
                    return config;
                },
                (error) => Promise.reject(error)
            );

            const responseInterceptor = axios.interceptors.response.use(
                (response) => response,
                async (error) => {
                    if (error.response?.status === 401 && !isRefreshing) {
                        console.log('🚨 Token expiré (401) détecté, tentative de renouvellement...');

                        // Vérifier qu'on n'est pas déjà en train de refresh
                        if (!isRefreshing) {
                            console.log('🔄 Lancement du refresh token depuis interceptor');
                            await regenerateAccessToken();
                        } else {
                            console.log('🛑 Refresh déjà en cours, ignorer ce 401');
                        }
                    } else if (error.response?.status === 401 && isRefreshing) {
                        console.log('🛑 401 reçu pendant refresh - ignorer pour éviter le spam');
                    }
                    return Promise.reject(error);
                }
            );

            return () => {
                axios.interceptors.request.eject(requestInterceptor);
                axios.interceptors.response.eject(responseInterceptor);
            };
        }
    }, [accessToken, regenerateAccessToken, isRefreshing])


    return (
        <TokenContext.Provider
            value={{
                accessToken,
                refreshToken,
                regenerateAccessToken,
                authState,
                startOAuthFlow,
                setCode,
                finishProcess,
                setFinishProcess,
                disconnect,
                clearAuth,
                authError,
                isLoading,
                isAuthenticated: !!accessToken && validateToken(accessToken)
            }}>
            {children}
        </TokenContext.Provider>
    );
};

export {TokenContext, TokenProvider};